import { flatMap } from 'lodash';
import { AdditionalClassificationDto } from '../types';

export const flattenClassifications = (definitions: AdditionalClassificationDto[]): AdditionalClassificationDto[] =>
    flatMap(definitions, definition => [definition, ...flattenClassifications(definition.children ?? [])]);

export function classificationPath(parent: string | undefined, key: string): string {
    return parent === undefined ? key : `${parent}/${key}`;
}

// Only duplicate sibling slots need an occurrence suffix. Unique-slot paths survive reordering.
export function classificationOccurrenceKeys(definitions: AdditionalClassificationDto[]): string[] {
    const counts = new Map<string, number>();
    const occurrences = new Map<string, number>();
    definitions.forEach(({ classificationSlot }) =>
        counts.set(classificationSlot, (counts.get(classificationSlot) ?? 0) + 1)
    );
    return definitions.map(({ classificationSlot }) => {
        const occurrence = occurrences.get(classificationSlot) ?? 0;
        occurrences.set(classificationSlot, occurrence + 1);
        return counts.get(classificationSlot)! > 1 ? `${classificationSlot}[${occurrence}]` : classificationSlot;
    });
}

// Stable sorting preserves occurrence order for duplicate slots, whose paths depend on that order.
export function classificationVerificationDefinitions(
    definitions: AdditionalClassificationDto[]
): AdditionalClassificationDto[] {
    return definitions
        .map(({ typeCode, classificationSlot, displayName, systemName, standardParent, children }) => ({
            typeCode,
            classificationSlot,
            displayName,
            systemName,
            ...(standardParent
                ? {
                      standardParent: {
                          typeCode: standardParent.typeCode,
                          subclassNumber: standardParent.subclassNumber,
                          displayName: standardParent.displayName,
                          ...(standardParent.inputId !== undefined ? { inputId: standardParent.inputId } : {})
                      }
                  }
                : {}),
            children: classificationVerificationDefinitions(children ?? [])
        }))
        .sort((a, b) =>
            a.classificationSlot < b.classificationSlot ? -1 : a.classificationSlot > b.classificationSlot ? 1 : 0
        );
}
